import React, { createContext, useContext, useEffect, useRef } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const defaultTheme = {
  palette: {
    secondary: {
      main: '#f0f0f0', // $goodman-grey[500]
      light: '#fafafa', // $goodman-grey[100]
      dark: '#d4d4d4', // $goodman-grey[700]
      contrastText: '#585858',
    },
    primary: {
      main: '#0a0a0a', // $goodman-black[500]
      light: '#585858', // $goodman-black[50]
      dark: '#3d3d3d', // $goodman-black[700]
      contrastText: '#ffffff',
    },
    error: {
      main: '#d66853', // From variables.css danger color
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#0a0a0a',
      secondary: '#585858',
    },
  },
};
const theme = createTheme({
  ...defaultTheme,
  typography: {
    fontFamily: 'Gotham Book, Arial, sans-serif',
    h1: {
      fontFamily: 'Gotham Black, serif',
      textTransform: 'uppercase',
      fontSize: '5.6vmin',
      lineHeight: '6.5vmin',
    },
    h2: {
      fontFamily: 'Libre Baskerville, serif',
      fontSize: '3.4vmin',
      lineHeight: '4.5vmin',
    },
    button: {
      fontFamily: 'Gotham Book, Arial, sans-serif',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#f0f0f0',
          color: '#0a0a0a',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '16px',
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        disableEscapeKeyDown: true,
        disablePortal: true,
      },
      styleOverrides: {
        paper: {
          backgroundColor: '#f0f0f0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: '8px',
          maxWidth: '300px',
          minWidth: '0px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          color: defaultTheme.palette.primary.light,
          padding: '16px',
          '&.MuiTableCell-body': {
            backgroundColor: defaultTheme.palette.secondary.main,
          },
        },
        head: {
          backgroundColor: defaultTheme.palette.secondary.main,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          color: defaultTheme.palette.primary.light,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderBottom: '3px solid',
          backgroundColor: defaultTheme.palette.secondary.main,
          '&:hover': {
            '& .MuiTableCell-body': {
              backgroundColor: defaultTheme.palette.secondary.main,
            },
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#0a0a0a', // Ensure form labels are visible
          '&.Mui-focused': {
            color: '#0a0a0a',
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: '#0a0a0a', // Ensure radio button labels are visible
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: defaultTheme.palette.secondary.main,
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          color: defaultTheme.palette.primary.light,
          '&.Mui-selected': {
            backgroundColor: defaultTheme.palette.primary.main,
            color: defaultTheme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: defaultTheme.palette.primary.dark,
            },
          },
          '&:hover': {
            backgroundColor: defaultTheme.palette.secondary.dark,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: defaultTheme.palette.primary.light,
        },
        selectLabel: {
          color: defaultTheme.palette.primary.light,
        },
        displayedRows: {
          color: defaultTheme.palette.primary.light,
        },
        select: {
          color: defaultTheme.palette.primary.light,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: defaultTheme.palette.secondary.main,
        },
        list: {
          color: defaultTheme.palette.primary.light,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          color: defaultTheme.palette.primary.light,
          '&:hover': {
            backgroundColor: defaultTheme.palette.secondary.dark,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: defaultTheme.palette.secondary.main,
        },
        option: {
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: defaultTheme.palette.secondary.dark,
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'white',
          },
        },
        groupLabel: {
          fontSize: '16px',
        },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Enable background refetching for better UX
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Create a context to expose the query client
const QueryClientContext = createContext<QueryClient | null>(null);

// Global calendar refresh event bus
type CalendarRefreshListener = (siteId?: number) => void;
const calendarRefreshListeners = new Set<CalendarRefreshListener>();

// Export the query client instance for external access
export { queryClient };

// Helper function to notify all calendar instances to refresh
export function notifyCalendarRefresh(siteId?: number) {
  calendarRefreshListeners.forEach((listener) => listener(siteId));
  // Also invalidate React Query cache for good measure
  invalidateActivitiesQueries(siteId);
}

// Hook to listen for global calendar refresh events
export function useCalendarRefreshListener(callback: CalendarRefreshListener) {
  const callbackRef = useRef(callback);

  // Update the ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const listener = (siteId?: number) => {
      callbackRef.current(siteId);
    };

    calendarRefreshListeners.add(listener);

    return () => {
      calendarRefreshListeners.delete(listener);
    };
  }, []);
}

// Helper function to invalidate activities queries from external components
export function invalidateActivitiesQueries(siteId?: number) {
  if (siteId) {
    // Invalidate queries for a specific site
    queryClient.invalidateQueries({
      queryKey: ['activities', siteId],
      refetchType: 'active',
    });
  } else {
    // Invalidate all activities queries
    queryClient.invalidateQueries({
      queryKey: ['activities'],
      refetchType: 'active',
    });
  }
}

// Helper function to refetch current activities queries
export function refetchActivitiesQueries(siteId?: number) {
  if (siteId) {
    return queryClient.refetchQueries({
      queryKey: ['activities', siteId],
      type: 'active',
    });
  } else {
    return queryClient.refetchQueries({
      queryKey: ['activities'],
      type: 'active',
    });
  }
}

// Hook to access the query client from React components
export function useQueryClient() {
  const client = useContext(QueryClientContext);
  if (!client) {
    throw new Error('useQueryClient must be used within ReactProvider');
  }
  return client;
}

export function ReactProvider({ children }: { children: React.ReactNode }) {
  // add other providers here as needed
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <QueryClientProvider client={queryClient}>
          <QueryClientContext.Provider value={queryClient}>
            {children}
          </QueryClientContext.Provider>
        </QueryClientProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
